import React, { lazy, Suspense, useEffect } from 'react';
import { useLocation, Route, Routes } from 'react-router-dom';
import { Box } from '@mui/material';

import Header from './components/Header';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import { ErrorProvider } from './context/ErrorContext';
import { useAuth } from './context/AuthContext';
import { HubConnectionBuilder } from "@microsoft/signalr";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Welcome = lazy(() => import('./pages/Welcome'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const ForgottenPassword = lazy(() => import('./pages/auth/ForgottenPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const Register = lazy(() => import('./pages/auth/Register'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const Account = lazy(() => import('./pages/Account'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Arrows = lazy(() => import('./pages/member_assets/Arrows'));
const Bows = lazy(() => import('./pages/member_assets/Bows'));
const SpineCalculator = lazy(() => import('./pages/member_assets/SpineCalculator'));
const BowMods = lazy(() => import('./pages/member_assets/BowMods'));
const SetupSnapshots = lazy(() => import('./pages/member_assets/SetupSnapshots'));
const PersonalRounds = lazy(() => import('./pages/member_assets/PersonalRounds'));
const Competitions = lazy(() => import('./pages/events/Competitions'));
const Practice = lazy(() => import('./pages/events/Practice'));
const TuningSessions = lazy(() => import('./pages/events/TuningSessions'));
const Bests = lazy(() => import('./pages/events/Bests'));
const DistanceBests = lazy(() => import('./pages/events/DistanceBests'));
const Handicap = lazy(() => import('./pages/events/Handicaps'));
const WhatScore = lazy(() => import('./pages/events/WhatScore'));
const MemberShares = lazy(() => import('./pages/member_shares/MemberShares'));
const SquadScores = lazy(() => import('./pages/member_shares/SquadScores'));
const SquadAbout = lazy(() => import('./pages/member_shares/SquadAbout'));
const Diary = lazy(() => import('./pages/Diary'));
const Members = lazy(() => import('./pages/admin/Members'));
const Broadcast = lazy(() => import('./pages/admin/Broadcast'));
const Help = lazy(() => import('./pages/Help'));

const App = () => {
  const location = useLocation();
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_HUB_URL;

  // 👇 Track route changes for Google Analytics
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  // 👇 SignalR notifications listener
  const NotificationsListener = ({ token }) => {
    useEffect(() => {
      if (!token) return; // only connect when logged in

      const connection = new HubConnectionBuilder()
        .withUrl(`${API_URL}/hubs/notifications`, {
          accessTokenFactory: () => token
        })
        .withAutomaticReconnect()
        .build();

      connection.on("ReceiveNotification", (data) => {
        const type = data?.type || "info";

        if (toast[type]) {
          toast[type](data.message);
        } else {
          toast(data.message);
        }
      });

      connection.start()
        .then(() => console.log("SignalR connected"))
        .catch(err => console.error("SignalR error:", err));

      return () => {
        connection.stop();
      };
    }, [token]);

    return null; // this component doesn’t render anything
  };

  return (
    <ErrorProvider>
      <NotificationsListener token={token} />
      <ToastContainer
        position="top-right"
        autoClose={10000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header />
        <Box sx={{ flex: 1 }}>
          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>Loading page…</Box>}>
            <Routes>
            {/* Public routes */}
            <Route path="/" element={<Welcome />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgottenpassword" element={<ForgottenPassword />} />
            <Route path="/resetpassword" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/help" element={<Help />} />

            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/account" element={<Account />} />
              <Route path="/diary" element={<Diary />} />
              <Route path="/arrows" element={<Arrows />} />
              <Route path="/bows" element={<Bows />} />
              <Route path="/spine-calculator" element={<SpineCalculator />} />
              <Route path="/bows/:bowId/mods" element={<BowMods />} />
              <Route path="/bows/:bowId/snapshots" element={<SetupSnapshots />} />
              <Route path="/personal-rounds" element={<PersonalRounds />} />
              <Route path="/competitions" element={<Competitions />} />
              <Route path="/practice" element={<Practice />} />
              <Route path="/tuning" element={<TuningSessions />} />
              <Route path="/bests" element={<Bests />} />
              <Route path="/distance-bests" element={<DistanceBests />} />
              <Route path="/handicaps" element={<Handicap />} />
              <Route path="/what-score" element={<WhatScore />} />
              <Route path="/members" element={<Members />} />
              <Route path="/broadcast" element={<Broadcast />} />
              <Route path="/member-shares" element={<MemberShares />} />
              <Route path="/squad-scores" element={<SquadScores />} />
              <Route path="/squad-about" element={<SquadAbout />} />
            </Route>
            </Routes>
          </Suspense>
        </Box>
        <Footer />
      </Box>
    </ErrorProvider>
  );
};

export default App;
